import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Layers, Search, RefreshCw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

export const CategoryStockSettings = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [saving, setSaving] = useState({});
  const [syncingNames, setSyncingNames] = useState(false);

  const handleSyncNames = async () => {
    setSyncingNames(true);
    try {
      const res = await axios.post(`${API}/category-settings/sync-names`);
      toast.success(`${res.data.families_synced} famílias sincronizadas do SAGE`);
      await fetchData();
    } catch (e) {
      toast.error('Erro ao sincronizar nomes do SAGE');
    } finally {
      setSyncingNames(false);
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/category-settings`);
      setData(res.data);
    } catch (e) {
      toast.error('Erro ao carregar categorias');
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleToggle = async (category, managed) => {
    setSaving(prev => ({ ...prev, [category]: true }));
    try {
      await axios.put(`${API}/category-settings`, { category, managed });
      setData(prev => ({
        ...prev,
        categories: prev.categories.map(c => c.category === category ? { ...c, managed } : c),
        managed_count: prev.managed_count + (managed ? 1 : -1),
      }));
      toast.success(`Categoria ${managed ? 'em gestão' : 'sem gestão'} de stock`);
    } catch (e) {
      toast.error('Erro ao actualizar categoria');
    } finally {
      setSaving(prev => ({ ...prev, [category]: false }));
    }
  };

  if (!data) return null;

  const filtered = data.categories.filter(c => {
    if (stateFilter === 'managed' && !c.managed) return false;
    if (stateFilter === 'unmanaged' && c.managed) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return c.label.toLowerCase().includes(q)
        || (c.description || '').toLowerCase().includes(q)
        || (c.group_code || '').toLowerCase().includes(q)
        || (c.group_description || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Agrupar por Grupo SAGE (cabeçalho "AA — Automóvel")
  const grouped = [];
  filtered.forEach(c => {
    const key = c.group_code || '__outros__';
    let g = grouped.find(x => x.key === key);
    if (!g) {
      g = {
        key,
        title: c.group_code
          ? `${c.group_code}${c.group_description ? ` — ${c.group_description}` : ''}`
          : 'Outros',
        items: [],
      };
      grouped.push(g);
    }
    g.items.push(c);
  });

  return (
    <Card className="border-2 border-cyan-200 rounded-sm shadow-none" data-testid="category-stock-settings">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Layers className="h-5 w-5 text-cyan-600" />
          Gestão de Stock por Categoria
          <Badge variant="secondary" className="ml-auto text-xs" data-testid="category-managed-count">
            {data.managed_count}/{data.total_categories} em gestão
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Por omissão nenhuma categoria é gerida (drop-shipping). Activa as categorias cujos artigos
          queres gerir e controlar na APP (alertas, cockpit, sugestões AI).
          Podes forçar a gestão de um artigo específico na ficha do produto.
          {data.override_count > 0 && (
            <span className="ml-1 text-cyan-700 font-medium">{data.override_count} artigo(s) com override individual.</span>
          )}
        </p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar categoria..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-9"
              data-testid="category-search-input"
            />
          </div>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="category-state-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="managed">Em gestão</SelectItem>
              <SelectItem value="unmanaged">Sem gestão</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={handleSyncNames}
            disabled={syncingNames}
            className="h-9 px-3 inline-flex items-center gap-1.5 text-xs border rounded-md hover:bg-slate-50 text-slate-600 whitespace-nowrap disabled:opacity-50"
            title="Sincronizar nomes de Grupos/Famílias do SAGE"
            data-testid="sync-category-names-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncingNames ? 'animate-spin' : ''}`} />
            Nomes SAGE
          </button>
        </div>
        <div className="border rounded-md max-h-[420px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground text-center">Nenhuma categoria encontrada</p>
          ) : grouped.map(g => (
            <div key={g.key}>
              <div className="sticky top-0 z-10 bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide border-y border-slate-200 first:border-t-0">
                {g.title}
              </div>
              <div className="divide-y">
                {g.items.map(c => (
                  <div key={c.category} className={`flex items-center justify-between gap-3 px-3 py-2.5 ${c.managed ? '' : 'opacity-70'}`} data-testid={`category-row-${c.category}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        <span className="font-mono text-xs text-slate-500 mr-1.5">{c.label}</span>
                        {c.description || c.label}
                      </p>
                      <p className="text-xs text-muted-foreground">{c.product_count} artigo{c.product_count !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs ${c.managed ? 'text-cyan-700 font-medium' : 'text-slate-400'}`}>
                        {c.managed ? 'Em gestão' : 'Sem gestão'}
                      </span>
                      <Switch
                        checked={c.managed}
                        disabled={saving[c.category]}
                        onCheckedChange={(v) => handleToggle(c.category, v)}
                        data-testid={`category-toggle-${c.category}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
